/**
 * MetricsPage — J1-B
 *
 * Metrics CRUD with:
 *   - Domain-filtered list
 *   - Inline current-value recording
 *   - Threshold config (warning / critical) via edit form
 *   - Recharts time-series chart (last 30 readings)
 *   - Threshold Events section (history entries where state ≠ ok)
 *
 * PRD §12.3.4 task 2.
 *
 * Sheet Migration Wave 4b (AC-W4b-02): MetricFormDialog uses FormSheet<MetricSnapshot>.
 * The component is defined inline below so all form-field strings remain in this file and
 * unit-test source-scan assertions continue to pass.
 *
 * All 11 inputs preserved: title, description, domain_id, indicator_type,
 * direction, cadence, target_value, threshold_warning, threshold_critical,
 * source_of_truth, status.
 *
 * urlKey="edit-metric" for deep-link routing.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Alert } from '@/components/ui/Alert';
import { FormSheet } from '@/components/ui/FormSheet';
import { showToast } from '@/components/ui/Toast';
import { Modal } from '@/components/ui/Modal/Modal';
import { Plus, BarChart2, BarChart3, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { t, tRoros } from '@/lib/i18n';
import { stripDomainId } from '@/lib/forms/stripFields';

// ─── i18n helper for seeded system metrics ────────────────────────────────────
//
// DOGFOOD-3: migration 290 stores FTT metric title/description as translation
// keys (e.g. "roros.metrics.ftt_baseline.name"). Resolve them through the
// roros:: namespace. User-authored titles are passed through unchanged.
function resolveMetricTitle(title: string): string {
  return title.startsWith('roros.') ? tRoros(title) : title;
}
import { formatDate } from '@/lib/localeFormatters';
import {
  useRorosMetrics,
  useRorosMetricCreate,
  useRorosMetricUpdate,
  useRorosMetricDelete,
  useRorosMetricUpdateValue,
  useRorosMetricHistory,
} from '@/hooks/roros/artifacts';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import type { RorosMetricDTO, RorosMetricValueDTO } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';

// Strategy domain integer ID (STRATEGY enum = 1).
const STRATEGY_DOMAIN_ID_INT = 1;
const METRIC_URL_KEY = 'edit-metric';

// ─── Status badge ──────────────────────────────────────────────────────────

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active': return 'success';
    case 'archived': return 'default';
    default: return 'warning';
  }
}

function thresholdVariant(state: string | null): 'success' | 'warning' | 'error' | 'default' {
  switch (state) {
    case 'ok': return 'success';
    case 'warning': return 'warning';
    case 'critical': return 'error';
    default: return 'default';
  }
}

// ─── Snapshot type & diff (Wave 4b) ──────────────────────────────────────────

type MetricSnapshot = {
  title: string;
  description: string;
  domainId: string;
  indicatorType: string;
  direction: string;
  cadence: string;
  targetValue: string;
  thresholdWarning: string;
  thresholdCritical: string;
  sourceOfTruth: string;
  status: string;
};

function diffMetricSnapshots(
  base: MetricSnapshot,
  cur: MetricSnapshot,
): Partial<Record<keyof MetricSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    description: base.description !== cur.description,
    domainId: base.domainId !== cur.domainId,
    indicatorType: base.indicatorType !== cur.indicatorType,
    direction: base.direction !== cur.direction,
    cadence: base.cadence !== cur.cadence,
    targetValue: base.targetValue !== cur.targetValue,
    thresholdWarning: base.thresholdWarning !== cur.thresholdWarning,
    thresholdCritical: base.thresholdCritical !== cur.thresholdCritical,
    sourceOfTruth: base.sourceOfTruth !== cur.sourceOfTruth,
    status: base.status !== cur.status,
  };
}

// ─── MetricFormDialog (inline, Wave 4b) ──────────────────────────────────────
//
// Inline here (not a separate file) so unit-test source-scan assertions on
// MetricsPage.tsx can verify that title, description, domain_id, indicator_type,
// direction, cadence, target_value, threshold_warning, threshold_critical,
// source_of_truth, status are present.

interface MetricFormDialogProps {
  mode: 'create' | 'edit';
  isOpen: boolean;
  onClose: () => void;
  domains: RorosDomainDTO[];
  editingMetric?: RorosMetricDTO;
  defaultDomainId: string;
  urlKey?: string;
  urlId?: string;
}

function MetricFormDialog({
  mode,
  isOpen,
  onClose,
  domains,
  editingMetric,
  defaultDomainId,
  urlKey,
  urlId,
}: MetricFormDialogProps) {
  const createMutation = useRorosMetricCreate();
  const updateMutation = useRorosMetricUpdate();

  const domainOptions = useMemo(
    () => domains.map((d) => ({ value: d.id, label: d.name })),
    [domains],
  );

  // ─── Form state via useFormSnapshot ─────────────────────────────────────
  const { state, setField, baseline, current } = useFormSnapshot<MetricSnapshot>({
    initialFactory: () =>
      mode === 'edit' && editingMetric
        ? {
            // TRANS-1: resolve i18n keys so the edit dialog shows human-readable strings.
            title: resolveMetricTitle(editingMetric.title),
            description: resolveMetricTitle(editingMetric.description ?? ''),
            domainId: editingMetric.domainId ?? defaultDomainId,
            indicatorType: editingMetric.indicatorType ?? 'lagging',
            direction: editingMetric.direction ?? 'higher_better',
            cadence: editingMetric.cadence ?? 'weekly',
            // Numeric fields stored as strings for Input compatibility; null → empty string.
            targetValue: editingMetric.targetValue != null ? String(editingMetric.targetValue) : '',
            thresholdWarning: editingMetric.thresholdWarning != null ? String(editingMetric.thresholdWarning) : '',
            thresholdCritical: editingMetric.thresholdCritical != null ? String(editingMetric.thresholdCritical) : '',
            sourceOfTruth: editingMetric.sourceOfTruth ?? '',
            status: editingMetric.status,
          }
        : {
            title: '',
            description: '',
            domainId: defaultDomainId,
            // Defaults must match createMetricSchema enums exactly:
            //   indicator_type: 'lagging', direction: 'higher_better', cadence: 'weekly'
            indicatorType: 'lagging',
            direction: 'higher_better',
            cadence: 'weekly',
            targetValue: '',
            thresholdWarning: '',
            thresholdCritical: '',
            sourceOfTruth: '',
            status: 'active',
          },
    isOpen,
    depKey: editingMetric?.id ?? null,
  });

  const {
    title,
    description,
    domainId,
    indicatorType,
    direction,
    cadence,
    targetValue,
    thresholdWarning,
    thresholdCritical,
    sourceOfTruth,
    status,
  } = state;

  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (isOpen) setError(null); }, [isOpen, editingMetric?.id]);

  // ─── Helpers: parse optional number field ───────────────────────────────
  const parseOptionalNum = (v: string): number | undefined =>
    v !== '' && !isNaN(parseFloat(v)) ? parseFloat(v) : undefined;

  // ─── Validation & save ───────────────────────────────────────────────────
  const handleSave = () => {
    setError(null);
    if (!title.trim()) {
      setError(t('Title is required'));
      return;
    }

    if (mode === 'edit' && editingMetric) {
      // Edit: domain cannot change; strip domain_id from payload.
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        indicator_type: indicatorType || undefined,
        direction: direction || undefined,
        cadence: cadence || undefined,
        target_value: parseOptionalNum(targetValue),
        threshold_warning: parseOptionalNum(thresholdWarning),
        threshold_critical: parseOptionalNum(thresholdCritical),
        source_of_truth: sourceOfTruth.trim() || undefined,
        status,
      };
      updateMutation.mutate(
        { domainId: editingMetric.domainId, id: editingMetric.id, payload: payload as any },
        {
          onSuccess: () => { showToast.success(t('Metric updated')); onClose(); },
          onError: (err: any) => setError(err?.message || t('Failed to save metric')),
        },
      );
    } else {
      // Create: use stripDomainId to separate domain_id (URL path) from body.
      const data: Record<string, unknown> = {
        domain_id: domainId,
        title: title.trim(),
        description: description.trim() || undefined,
        indicator_type: indicatorType || undefined,
        direction: direction || undefined,
        cadence: cadence || undefined,
        target_value: parseOptionalNum(targetValue),
        threshold_warning: parseOptionalNum(thresholdWarning),
        threshold_critical: parseOptionalNum(thresholdCritical),
        source_of_truth: sourceOfTruth.trim() || undefined,
        status,
      };
      const { domain_id, rest } = stripDomainId(data);
      const effectiveDomainId = domain_id || defaultDomainId;
      createMutation.mutate(
        { domainId: effectiveDomainId, payload: rest as any },
        {
          onSuccess: () => { showToast.success(t('Metric created')); onClose(); },
          onError: (err: any) => setError(err?.message || t('Failed to save metric')),
        },
      );
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <FormSheet<MetricSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? t('Edit Metric') : t('New Metric')}
      mode={mode}
      baseline={baseline}
      current={current}
      diff={diffMetricSnapshots}
      onSubmit={handleSave}
      isSubmitting={isPending}
      submitLabel={mode === 'edit' ? t('Save') : t('Create Metric')}
      submittingLabel={t('Saving…')}
      submitTestId="metric-save-button"
      width="md"
      urlKey={urlKey}
      urlId={urlId}
    >
      <div className="space-y-4">
        {error && (
          <Alert variant="error" data-testid="metric-error">
            {error}
          </Alert>
        )}

        {/* 1. Title */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-title">
            {t('Title')} <span className="text-destructive">*</span>
          </label>
          <Input
            id="metric-title"
            value={title}
            onChange={(e) => setField('title', e.target.value)}
            placeholder={t('e.g. FTT Rate')}
            required
            data-testid="metric-title-input"
          />
        </div>

        {/* 2. Description */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-description">
            {t('Description')}
          </label>
          <Textarea
            id="metric-description"
            value={description}
            onChange={(e) => setField('description', e.target.value)}
            rows={3}
            data-testid="metric-description-input"
          />
        </div>

        {/* 3. Domain */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-domain">
            {t('Domain')}
          </label>
          <Select
            id="metric-domain"
            value={domainId}
            onChange={(e) => setField('domainId', e.target.value)}
            options={domainOptions}
            data-testid="metric-domain-select"
          />
        </div>

        {/* 4. Indicator Type */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-indicator-type">
            {t('Indicator Type')}
          </label>
          <Select
            id="metric-indicator-type"
            value={indicatorType}
            onChange={(e) => setField('indicatorType', e.target.value)}
            options={[
              // Values must match createMetricSchema: z.enum(['leading', 'lagging'])
              { value: 'lagging', label: t('Lagging') },
              { value: 'leading', label: t('Leading') },
            ]}
            data-testid="metric-indicator-type-select"
          />
        </div>

        {/* 5. Direction */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-direction">
            {t('Direction')}
          </label>
          <Select
            id="metric-direction"
            value={direction}
            onChange={(e) => setField('direction', e.target.value)}
            options={[
              // Values must match createMetricSchema: z.enum(['higher_better', 'lower_better', 'target'])
              { value: 'higher_better', label: t('Higher is better') },
              { value: 'lower_better', label: t('Lower is better') },
              { value: 'target', label: t('Hit a target') },
            ]}
            data-testid="metric-direction-select"
          />
        </div>

        {/* 6. Cadence */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-cadence">
            {t('Cadence')}
          </label>
          <Select
            id="metric-cadence"
            value={cadence}
            onChange={(e) => setField('cadence', e.target.value)}
            options={[
              { value: 'daily', label: t('Daily') },
              { value: 'weekly', label: t('Weekly') },
              { value: 'monthly', label: t('Monthly') },
            ]}
            data-testid="metric-cadence-select"
          />
        </div>

        {/* 7. Target Value */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-target-value">
            {t('Target Value')}
          </label>
          <Input
            id="metric-target-value"
            type="number"
            step="any"
            value={targetValue}
            onChange={(e) => setField('targetValue', e.target.value)}
            data-testid="metric-target-value-input"
          />
        </div>

        {/* 8. Warning Threshold */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-threshold-warning">
            {t('Warning Threshold')}
          </label>
          <Input
            id="metric-threshold-warning"
            type="number"
            step="any"
            value={thresholdWarning}
            onChange={(e) => setField('thresholdWarning', e.target.value)}
            data-testid="metric-threshold-warning-input"
          />
        </div>

        {/* 9. Critical Threshold */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-threshold-critical">
            {t('Critical Threshold')}
          </label>
          <Input
            id="metric-threshold-critical"
            type="number"
            step="any"
            value={thresholdCritical}
            onChange={(e) => setField('thresholdCritical', e.target.value)}
            data-testid="metric-threshold-critical-input"
          />
        </div>

        {/* 10. Source of Truth URL */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-source-of-truth">
            {t('Source of Truth URL')}
          </label>
          <Input
            id="metric-source-of-truth"
            type="text"
            value={sourceOfTruth}
            onChange={(e) => setField('sourceOfTruth', e.target.value)}
            placeholder="https://..."
            data-testid="metric-source-of-truth-input"
          />
        </div>

        {/* 11. Status */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="metric-status">
            {t('Status')}
          </label>
          <Select
            id="metric-status"
            value={status}
            onChange={(e) => setField('status', e.target.value)}
            options={[
              { value: 'active', label: t('active') },
              { value: 'archived', label: t('archived') },
            ]}
            data-testid="metric-status-select"
          />
        </div>
      </div>
    </FormSheet>
  );
}

// ─── Record Value Modal ────────────────────────────────────────────────────

interface RecordValueModalProps {
  metric: RorosMetricDTO;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: number) => void;
  isLoading: boolean;
}

function RecordValueModal({ metric, isOpen, onClose, onSubmit, isLoading }: RecordValueModalProps) {
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    const num = parseFloat(value);
    if (!isNaN(num)) onSubmit(num);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('Record Value')} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('Metric')}: <span className="font-medium text-foreground">{resolveMetricTitle(metric.title)}</span>
        </p>
        {metric.currentValue != null && (
          <p className="text-xs text-muted-foreground">
            {t('Current')}: {metric.currentValue}
            {metric.targetValue != null && ` / ${t('Target')}: ${metric.targetValue}`}
          </p>
        )}
        <Input
          label={t('New Value')}
          type="number"
          step="any"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
          autoFocus
          data-testid="metric-value-input"
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
            {t('Cancel')}
          </Button>
          <Button type="button" onClick={handleSubmit} isLoading={isLoading} data-testid="metric-value-submit">
            {t('Record')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Metric Detail Modal (chart + threshold events) ─────────────────────

interface MetricDetailModalProps {
  metric: RorosMetricDTO;
  isOpen: boolean;
  onClose: () => void;
}

function MetricDetailModal({ metric, isOpen, onClose }: MetricDetailModalProps) {
  const historyQuery = useRorosMetricHistory(metric.domainId, metric.id, 30);
  const history = (historyQuery.data ?? []) as RorosMetricValueDTO[];

  const chartData = useMemo(
    () =>
      [...history].reverse().map((h) => ({
        date: formatDate(h.recordedAt),
        value: h.value,
        state: h.thresholdState,
      })),
    [history],
  );

  const thresholdEvents = useMemo(
    () => history.filter((h) => h.thresholdState !== 'ok'),
    [history],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={resolveMetricTitle(metric.title)} size="2xl">
      <div className="space-y-6">
        {/* ─── Chart ──────────────────────────────────────────────────── */}
        <div>
          <h3 className="text-sm font-medium mb-3">{t('Value History')}</h3>
          {historyQuery.isPending ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
              {t('Loading...')}
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
              {t('No data yet')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  className="fill-muted-foreground"
                />
                <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                <RechartTooltip
                  contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                  labelStyle={{ color: 'var(--foreground)' }}
                />
                {metric.thresholdWarning != null && (
                  <ReferenceLine
                    y={metric.thresholdWarning}
                    stroke="var(--warning)"
                    strokeDasharray="4 2"
                    label={{ value: t('Warning'), fill: 'var(--warning)', fontSize: 10 }}
                  />
                )}
                {metric.thresholdCritical != null && (
                  <ReferenceLine
                    y={metric.thresholdCritical}
                    stroke="var(--destructive)"
                    strokeDasharray="4 2"
                    label={{ value: t('Critical'), fill: 'var(--destructive)', fontSize: 10 }}
                  />
                )}
                {metric.targetValue != null && (
                  <ReferenceLine
                    y={metric.targetValue}
                    stroke="var(--primary)"
                    strokeDasharray="4 2"
                    label={{ value: t('Target'), fill: 'var(--primary)', fontSize: 10 }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ─── Threshold Events ──────────────────────────────────────── */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warning" />
            {t('Threshold Events')}
          </h3>
          {thresholdEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('No threshold events')}</p>
          ) : (
            <div className="space-y-2" role="list" aria-label={t('Threshold Events')}>
              {thresholdEvents.map((ev) => (
                <div
                  key={ev.id}
                  role="listitem"
                  className="flex items-center justify-between text-sm border border-border rounded-md px-3 py-2"
                >
                  <span className="text-muted-foreground">{formatDate(ev.recordedAt)}</span>
                  <span className="font-medium">{ev.value}</span>
                  <Badge variant={thresholdVariant(ev.thresholdState)}>
                    {t(ev.thresholdState)}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Metric Row ────────────────────────────────────────────────────────────

interface MetricRowProps {
  metric: RorosMetricDTO;
  domains: RorosDomainDTO[];
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onRecordValue: () => void;
  onViewDetail: () => void;
}

function MetricRow({ metric, domains, canEdit, canDelete, onEdit, onDelete, onRecordValue, onViewDetail }: MetricRowProps) {
  const domain = domains.find((d) => d.id === metric.domainId);

  return (
    <Card
      className="p-4 cursor-pointer"
      role="listitem"
      tabIndex={0}
      aria-label={resolveMetricTitle(metric.title)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onViewDetail(); }}
      onClick={(e) => {
        // FW9 fix: mouse click opens the detail drawer (PRD §12.3.4 J1 task 2 —
        // "Detail drawer with recorded-value time series chart"). Previously
        // only the keyboard path (Enter/Space) was wired; mouse users got
        // nothing. Sven UT-5 reproduction confirmed via Playwright.
        //
        // Don't fire onViewDetail when the click originated on an action
        // button inside the row (Record value / Edit / Delete) — those have
        // their own onClick handlers that should not also open the drawer.
        const target = e.target as HTMLElement;
        if (target.closest('button')) return;
        onViewDetail();
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm truncate">{resolveMetricTitle(metric.title)}</h3>
            <Badge variant={statusVariant(metric.status)}>{t(metric.status)}</Badge>
            {metric.thresholdState && metric.thresholdState !== 'ok' && (
              <Badge variant={thresholdVariant(metric.thresholdState)}>
                {t(metric.thresholdState)}
              </Badge>
            )}
          </div>

          {metric.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{resolveMetricTitle(metric.description)}</p>
          )}

          <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
            {domain && (
              <span>{t('Domain')}: <span className="font-medium text-foreground">{domain.name}</span></span>
            )}
            {metric.currentValue != null && (
              <span>
                {t('Value')}: <span className="font-medium text-foreground">{metric.currentValue}</span>
                {metric.targetValue != null && (
                  <> / {t('Target')}: <span className="font-medium text-foreground">{metric.targetValue}</span></>
                )}
              </span>
            )}
            {metric.cadence && (
              <span>{t('Cadence')}: <span className="font-medium text-foreground">{t(metric.cadence)}</span></span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRecordValue}
              aria-label={t('Record value')}
              data-testid={`record-value-${metric.id}`}
            >
              <BarChart2 className="h-3.5 w-3.5" />
            </Button>
          )}
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              aria-label={t('Edit')}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              aria-label={t('Delete')}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────

export function MetricsPage() {
  // ─── Permissions ────────────────────────────────────────────────────────
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_CREATE);
  const canEdit = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_EDIT);
  const canDelete = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_DELETE);

  // ─── State ─────────────────────────────────────────────────────────────
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState<RorosMetricDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [recordValueTarget, setRecordValueTarget] = useState<RorosMetricDTO | null>(null);
  const [detailTarget, setDetailTarget] = useState<RorosMetricDTO | null>(null);
  const [filterDomainId, setFilterDomainId] = useState<string>('');

  // ─── Data ──────────────────────────────────────────────────────────────
  const domainsQuery = useRorosDomains();
  const domains = (domainsQuery.data ?? []).filter((d: RorosDomainDTO) => d.isActive);

  const strategyDomain = useMemo(
    () => domains.find((d) => d.domainId === STRATEGY_DOMAIN_ID_INT) ?? null,
    [domains],
  );

  const activeDomainId = filterDomainId || (strategyDomain?.id ?? (domains[0]?.id ?? ''));
  const domainOptions = useMemo(
    () => domains.map((d) => ({ value: d.id, label: d.name })),
    [domains],
  );

  const metricsQuery = useRorosMetrics(activeDomainId);
  const metrics = (metricsQuery.data ?? []) as RorosMetricDTO[];

  // ─── Mutations ─────────────────────────────────────────────────────────
  const deleteMutation = useRorosMetricDelete();
  const recordValueMutation = useRorosMetricUpdateValue();

  // ─── Handlers ──────────────────────────────────────────────────────────
  const openCreate = useCallback(() => {
    setEditingMetric(null);
    setIsFormOpen(true);
  }, []);

  const openEdit = useCallback((metric: RorosMetricDTO) => {
    setEditingMetric(metric);
    setIsFormOpen(true);
  }, []);

  // ─── URL routing (?edit-metric=<id>) ───────────────────────────────────
  const getMetricId = useCallback((metric: RorosMetricDTO) => metric.id, []);
  const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: METRIC_URL_KEY,
    items: metrics,
    getId: getMetricId,
  });

  useEffect(() => {
    if (!deepLinkTarget) return;
    setEditingMetric(deepLinkTarget);
    setIsFormOpen(true);
    consumeDeepLink();
  }, [deepLinkTarget, consumeDeepLink]);

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    const metric = metrics.find((m) => m.id === deleteTarget.id);
    if (!metric) return;
    deleteMutation.mutate(
      { domainId: metric.domainId, id: deleteTarget.id },
      {
        onSuccess: () => { showToast.success(t('Metric deleted')); setDeleteTarget(null); },
        onError: (err) => showToast.error(err.message),
      },
    );
  }, [deleteTarget, deleteMutation, metrics]);

  const handleRecordValue = useCallback(
    (value: number) => {
      if (!recordValueTarget) return;
      recordValueMutation.mutate(
        { domainId: recordValueTarget.domainId, id: recordValueTarget.id, payload: { current_value: value } },
        {
          onSuccess: () => { showToast.success(t('Value recorded')); setRecordValueTarget(null); },
          onError: (err) => showToast.error(err.message),
        },
      );
    },
    [recordValueTarget, recordValueMutation],
  );

  const emptyCTA = canCreate ? (
    <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />} data-testid="metrics-empty-cta">
      {t('Add your first Metric')}
    </Button>
  ) : undefined;

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <RorosPage
      title={t('Metrics')}
      icon={<BarChart3 className="w-5 h-5" />}
      breadcrumbs={[{ label: t('Metrics'), path: '/roros/artifacts/metrics' }]}
      actions={
        canCreate ? (
          <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />} data-testid="new-metric-button">
            {t('New Metric')}
          </Button>
        ) : undefined
      }
    >
      {/* ─── Domain filter ──────────────────────────────────────────── */}
      <div role="group" className="flex flex-wrap gap-3 mb-4" aria-label={t('Metric filters')}>
        <div className="w-44">
          <Select
            id="metric-domain-filter"
            value={filterDomainId || 'all'}
            onChange={(e) => setFilterDomainId(e.target.value === 'all' ? '' : e.target.value)}
            aria-label={t('Filter by domain')}
            options={[
              { value: 'all', label: strategyDomain ? strategyDomain.name : t('All Domains') },
              ...domainOptions,
            ]}
          />
        </div>
      </div>

      {/* ─── Metrics list ──────────────────────────────────────────── */}
      <QueryPage
        query={metricsQuery as any}
        emptyMessage={t('No metrics yet')}
        emptyIcon={<BarChart2 className="h-8 w-8 text-muted-foreground" />}
        emptyAction={emptyCTA}
      >
        {(items) => (
          <div className="space-y-3" role="list" aria-label={t('Metrics')}>
            {(items as RorosMetricDTO[]).map((metric) => (
              <MetricRow
                key={metric.id}
                metric={metric}
                domains={domains}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={() => openEdit(metric)}
                onDelete={() => setDeleteTarget({ id: metric.id, title: resolveMetricTitle(metric.title) })}
                onRecordValue={() => setRecordValueTarget(metric)}
                onViewDetail={() => setDetailTarget(metric)}
              />
            ))}
          </div>
        )}
      </QueryPage>

      {/* ─── Create/Edit FormSheet (Wave 4b) ─────────────────────────── */}
      <MetricFormDialog
        mode={editingMetric ? 'edit' : 'create'}
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingMetric(null); }}
        domains={domains}
        editingMetric={editingMetric ?? undefined}
        defaultDomainId={activeDomainId}
        urlKey={editingMetric ? METRIC_URL_KEY : undefined}
        urlId={editingMetric?.id}
      />

      {/* ─── Record value modal ─────────────────────────────────────── */}
      {recordValueTarget && (
        <RecordValueModal
          metric={recordValueTarget}
          isOpen={!!recordValueTarget}
          onClose={() => setRecordValueTarget(null)}
          onSubmit={handleRecordValue}
          isLoading={recordValueMutation.isPending}
        />
      )}

      {/* ─── Detail / chart modal ───────────────────────────────────── */}
      {detailTarget && (
        <MetricDetailModal
          metric={detailTarget}
          isOpen={!!detailTarget}
          onClose={() => setDetailTarget(null)}
        />
      )}

      {/* ─── Delete confirm ─────────────────────────────────────────── */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('Delete this metric?')}
        description={deleteTarget ? `"${deleteTarget.title}"` : ''}
        confirmText={t('Delete')}
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </RorosPage>
  );
}
